'use client';

import { useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  BellRing,
  CheckCheck,
  ChevronLeft,
  ExternalLink,
  Mic,
  Phone,
  Plus,
  Reply,
  Video,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  PhoneFrame,
  TapRipple,
  TypingDots,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * WhatsApp AI concierge: a customer asks for a court, the AI offers three
 * times as reply buttons, the customer taps one, receives a payment link,
 * pays, gets a confirmation and a scheduled reminder. The chat is anchored to
 * the bottom so older messages scroll up naturally as the thread grows.
 */

const DURATIONS = [700, 1200, 1100, 1700, 800, 1000, 900, 1700, 1700, 2600] as const;

const STEP = {
  ask: 1,
  typing: 2,
  options: 3,
  tap: 4,
  reply: 5,
  typingAgain: 6,
  payLink: 7,
  paid: 8,
  reminder: 9,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of the WhatsApp AI concierge: a customer asks for a padel court, picks a time from reply buttons, pays through a link and receives a confirmation and reminder.',
    ar: 'عرض متحرك لمساعد واتساب الذكي: عميل يسأل عن ملعب بادل، ويختار الوقت من أزرار الرد، ويدفع عبر رابط، ثم يستلم التأكيد والتذكير.',
  },
  business: { en: 'Business account', ar: 'حساب أعمال' },
  today: { en: 'Today', ar: 'اليوم' },
  ask: {
    en: 'Hi! Any padel court free tomorrow around 8pm?',
    ar: 'السلام عليكم، عندكم ملعب بادل فاضي بكرة الساعة 8 تقريبًا؟',
  },
  options: {
    en: 'Hi Sara! Tomorrow (Thu) we have courts at these times. Which one works for you?',
    ar: 'وعليكم السلام سارة! هذه الأوقات المتاحة بكرة الخميس، أي وقت يناسبك؟',
  },
  pickedIntro: {
    en: 'Great choice! Court 3 is yours once the payment goes through.',
    ar: 'اختيار موفق! الملعب 3 محجوز لك بمجرد إتمام الدفع.',
  },
  linkTitle: { en: 'Court 3 · Thu 8:00 PM', ar: 'الملعب 3 · الخميس 8:00 م' },
  linkSubtitle: { en: 'Secure payment link', ar: 'رابط دفع آمن' },
  pay: { en: 'Pay to confirm', ar: 'ادفع لتأكيد الحجز' },
  paid: {
    en: "Payment received, you're booked. See you on court!",
    ar: 'وصلنا الدفع وحجزك مؤكد. نشوفك في الملعب!',
  },
  summaryCourt: { en: 'Court 3 · Panoramic', ar: 'الملعب 3 · بانورامي' },
  summaryWhen: { en: 'Thu 12 · 8:00 to 9:30 PM', ar: 'الخميس 12 · 8:00 إلى 9:30 م' },
  reminder: {
    en: 'Reminder scheduled for tomorrow, 6:00 PM',
    ar: 'سيصلك تذكير بكرة الساعة 6:00 م',
  },
  message: { en: 'Message', ar: 'اكتب رسالة' },
  t1: { en: '9:38 PM', ar: '9:38 م' },
  t2: { en: '9:39 PM', ar: '9:39 م' },
  t3: { en: '9:40 PM', ar: '9:40 م' },
  t4: { en: '9:41 PM', ar: '9:41 م' },
} as const;

const TIMES = [
  { en: '7:00 PM', ar: '7:00 م' },
  { en: '8:00 PM', ar: '8:00 م' },
  { en: '9:30 PM', ar: '9:30 م' },
] as const;

const PICKED = 1;

/*
 * Rows grow from zero height rather than using layout/popLayout: in a
 * bottom-anchored stack that keeps every transition overlap-free, exactly
 * like a real chat scrolling up.
 */
const ROW = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: {
    height: 0,
    opacity: 0,
    transition: { height: { duration: 0.3, ease: EASE_OUT_EXPO }, opacity: { duration: 0.12 } },
  },
  transition: {
    height: { duration: 0.42, ease: EASE_OUT_EXPO },
    opacity: { duration: 0.28, delay: 0.08 },
  },
} as const;

export function WhatsAppDemo({ locale, className }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS);
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.padel;
  const step = timeline.step;

  const messages: { key: string; node: React.ReactNode }[] = [];

  messages.push({
    key: 'date',
    node: (
      <div className="flex justify-center">
        <span className="rounded-md bg-white/90 px-2 py-0.5 text-[9.5px] font-medium text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
          {copy.today}
        </span>
      </div>
    ),
  });

  if (step >= STEP.ask) {
    messages.push({
      key: 'ask',
      node: (
        <Bubble side="me" time={copy.t1}>
          {copy.ask}
        </Bubble>
      ),
    });
  }

  if (step === STEP.typing || step === STEP.typingAgain) {
    messages.push({
      key: `typing-${step}`,
      node: (
        <div className="flex">
          <span className="rounded-[10px] rounded-ss-[2px] bg-white px-3 py-2.5 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
            <TypingDots color="#8696a0" />
          </span>
        </div>
      ),
    });
  }

  if (step >= STEP.options) {
    messages.push({
      key: 'options',
      node: (
        <div className="flex">
          <div className="w-[196px] overflow-hidden rounded-[10px] rounded-ss-[2px] bg-white shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
            <p className="px-2.5 pb-1 pt-2 text-[11.5px] leading-[1.4] text-[#111b21]">
              {copy.options}
            </p>
            <Stamp time={copy.t2} />
            {TIMES.map((time, index) => {
              const picked = index === PICKED && step >= STEP.tap;

              return (
                <motion.div
                  key={time.en}
                  className="relative flex h-[30px] items-center justify-center gap-1.5 overflow-hidden border-t border-[#e9edef] text-[11.5px] font-medium text-[#027eb5]"
                  initial={false}
                  animate={{ backgroundColor: picked ? '#e7f3f8' : '#ffffff' }}
                  transition={{ duration: 0.25 }}
                >
                  <Reply className="h-3 w-3 rtl:-scale-x-100" strokeWidth={2.2} />
                  {time[locale]}
                  <TapRipple
                    key={`tap-${timeline.cycle}`}
                    show={index === PICKED && step === STEP.tap}
                    className="left-1/2 top-1/2"
                    color="rgba(2,126,181,0.22)"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      ),
    });
  }

  if (step >= STEP.reply) {
    messages.push({
      key: 'reply',
      node: (
        <Bubble side="me" time={copy.t3}>
          {TIMES[PICKED]![locale]}
        </Bubble>
      ),
    });
  }

  if (step >= STEP.payLink) {
    messages.push({
      key: 'pay-link',
      node: (
        <div className="flex">
          <div className="w-[204px] overflow-hidden rounded-[10px] rounded-ss-[2px] bg-white p-1 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
            <div
              className="flex items-center gap-2 rounded-[7px] p-2"
              style={{ background: brand.accentSoft }}
            >
              <BrandMark brand={brand} size={30} />
              <div className="min-w-0">
                <p className="truncate text-[11px] font-semibold text-[#111b21]">
                  {brand.name[locale]}
                </p>
                <p className="truncate text-[9.5px] text-[#54656f]">{copy.linkSubtitle}</p>
              </div>
            </div>
            <p className="px-1.5 pt-1.5 text-[11.5px] font-semibold text-[#111b21]">
              {copy.linkTitle}
            </p>
            <p className="px-1.5 pb-0.5 pt-0.5 text-[11.5px] leading-[1.4] text-[#111b21]">
              {copy.pickedIntro}
            </p>
            <Stamp time={copy.t3} />
            <div className="flex h-[30px] items-center justify-center gap-1.5 border-t border-[#e9edef] text-[11.5px] font-medium text-[#027eb5]">
              <ExternalLink className="h-3 w-3" strokeWidth={2.2} />
              {copy.pay}
            </div>
          </div>
        </div>
      ),
    });
  }

  if (step >= STEP.paid) {
    messages.push({
      key: 'paid',
      node: (
        <div className="flex">
          <div className="w-[204px] rounded-[10px] rounded-ss-[2px] bg-white p-2 shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
            <p className="text-[11.5px] leading-[1.4] text-[#111b21]">{copy.paid}</p>
            <div className="mt-1.5 flex items-center gap-2 rounded-[7px] bg-[#f5f6f6] px-2 py-1.5">
              <CheckBadge show={step >= STEP.paid} size={20} bg={DEMO_COLORS.whatsapp} delay={0.3} />
              <div className="min-w-0">
                <p className="truncate text-[10.5px] font-semibold text-[#111b21]">
                  {copy.summaryCourt}
                </p>
                <p className="truncate text-[9.5px] text-[#54656f]">{copy.summaryWhen}</p>
              </div>
            </div>
            <Stamp time={copy.t4} inline={false} />
          </div>
        </div>
      ),
    });
  }

  if (step >= STEP.reminder) {
    messages.push({
      key: 'reminder',
      node: (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[#fff5c4] px-2 py-1 text-[9.5px] font-medium text-[#54656f] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]">
            <BellRing className="h-3 w-3" strokeWidth={2.2} />
            {copy.reminder}
          </span>
        </div>
      ),
    });
  }

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <PhoneFrame statusTone="light" screenClassName="bg-[#008069]">
        {/* Header */}
        <div className="absolute inset-x-0 top-0 z-20 flex h-[100px] items-end bg-[#008069] px-2 pb-2.5 text-white">
          <div className="flex w-full items-center gap-1.5">
            <ChevronLeft className="h-5 w-5 shrink-0 rtl:rotate-180" strokeWidth={2.2} />
            <span className="overflow-hidden rounded-full">
              <BrandMark brand={brand} size={32} />
            </span>
            <div className="min-w-0 flex-1 ps-1">
              <p className="flex items-center gap-1 text-[13px] font-semibold leading-tight">
                <span className="truncate">{brand.name[locale]}</span>
                <VerifiedBadge />
              </p>
              <p className="text-[10px] text-white/75">{copy.business}</p>
            </div>
            <Video className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            <Phone className="ms-3 me-1 h-4 w-4 shrink-0" strokeWidth={2} />
          </div>
        </div>

        {/* Chat wall */}
        <div
          className="absolute inset-x-0 bottom-[58px] top-[100px] overflow-hidden"
          style={{
            backgroundColor: DEMO_COLORS.whatsappWall,
            backgroundImage:
              'radial-gradient(rgba(84,101,111,0.07) 1px, transparent 1px)',
            backgroundSize: '12px 12px',
          }}
        >
          <div className="absolute inset-x-0 bottom-0 flex flex-col px-2.5 pb-2.5">
            <AnimatePresence initial={false}>
              {messages.map((message) => (
                <motion.div key={message.key} className="overflow-hidden" {...ROW}>
                  <div className="pb-[1px] pt-1.5">{message.node}</div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Composer */}
        <div className="absolute inset-x-0 bottom-0 z-20 flex h-[58px] items-start gap-2 bg-[#f0f2f5] px-2 pt-2">
          <Plus className="mt-1.5 h-5 w-5 shrink-0 text-[#54656f]" strokeWidth={2} />
          <span className="flex h-8 flex-1 items-center rounded-full bg-white px-3 text-[11px] text-[#8696a0]">
            {copy.message}
          </span>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#008069] text-white">
            <Mic className="h-4 w-4" strokeWidth={2.2} />
          </span>
        </div>
      </PhoneFrame>
    </DemoStage>
  );
}

function Bubble({
  side,
  time,
  children,
}: React.PropsWithChildren<{ side: 'me' | 'them'; time: string }>) {
  const mine = side === 'me';

  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[200px] rounded-[10px] px-2.5 pb-1 pt-1.5 text-[11.5px] leading-[1.4] text-[#111b21] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)]',
          mine ? 'rounded-se-[2px] bg-[#d9fdd3]' : 'rounded-ss-[2px] bg-white',
        )}
      >
        {children}
        <span className="float-end ms-2 mt-1.5 inline-flex items-center gap-0.5 text-[8.5px] text-[#667781]">
          {time}
          {mine && <CheckCheck className="h-3 w-3 text-[#53bdeb]" strokeWidth={2.4} />}
        </span>
      </div>
    </div>
  );
}

function Stamp({ time, inline = true }: { time: string; inline?: boolean }) {
  return (
    <p
      className={cn(
        'text-end text-[8.5px] text-[#667781]',
        inline ? 'px-2.5 pb-1.5' : 'mt-1',
      )}
    >
      {time}
    </p>
  );
}

function VerifiedBadge() {
  return (
    <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[#25d366] ring-1 ring-white/70">
      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" aria-hidden="true">
        <path
          d="M6 12.5l4 4L18 8"
          fill="none"
          stroke="#fff"
          strokeWidth={3.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
